import React, { useId, useMemo } from 'react';
import { Chart as ChartJS, Filler, Legend, LineElement, PointElement, RadialLinearScale, Tooltip } from 'chart.js';
import { Radar } from 'react-chartjs-2';
import { formatRadarPercent } from '../../../utils/radarUtils';
import './RadarChart.css';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const RadarChart = ({ title, data, ariaLabel, tableCaption, emptyMessage = 'Dados insuficientes para gerar o radar.' }) => {
  const reactId = useId().replace(/:/g, '');
  const tableId = `radar-data-${reactId}`;

  const hasData = data?.labels?.length >= 3 && data?.datasets?.length > 0;

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 12,
        right: 22,
        bottom: 12,
        left: 22,
      },
    },
    scales: {
      r: {
        min: 0,
        max: 1,
        beginAtZero: true,
        ticks: {
          stepSize: 0.2,
          backdropColor: 'rgba(255, 255, 255, 0.72)',
          callback: (value) => `${Math.round(value * 100)}%`,
        },
        pointLabels: {
          color: '#334155',
          font: {
            size: 12,
            weight: '600',
          },
        },
        grid: {
          color: 'rgba(148, 163, 184, 0.35)',
        },
        angleLines: {
          color: 'rgba(148, 163, 184, 0.35)',
        },
      },
    },
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        display: true,
        position: 'top',
        labels: {
          boxWidth: 14,
          boxHeight: 14,
          color: '#334155',
          font: { size: 12, weight: '600' },
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${formatRadarPercent(context.raw)}`,
        },
      },
    },
  }), []);

  if (!hasData) {
    return (
      <div className="radar-chart-card">
        {title && <h3>{title}</h3>}
        <div className="radar-empty">{emptyMessage}</div>
      </div>
    );
  }

  return (
    <div className="radar-chart-card">
      {title && <h3>{title}</h3>}
      <div className="radar-canvas-wrap">
        <Radar
          data={data}
          options={options}
          aria-label={ariaLabel || title || 'Gráfico de radar'}
          aria-describedby={tableId}
          role="img"
        />
      </div>
      <details className="radar-data-details">
        <summary>Ver dados do gráfico em tabela</summary>
        <div className="radar-table-scroll">
          <table id={tableId} className="radar-data-table">
            <caption>{tableCaption || title || 'Dados do gráfico de radar'}</caption>
            <thead>
              <tr>
                <th>Eixo</th>
                {data.datasets.map((dataset) => (
                  <th key={dataset.label}>{dataset.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.labels.map((label, labelIndex) => (
                <tr key={label}>
                  <td>{label}</td>
                  {data.datasets.map((dataset) => (
                    <td key={`${dataset.label}-${label}`}>{formatRadarPercent(dataset.data[labelIndex])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
};

export default RadarChart;
